import { useEffect, useRef, useState } from "react";
import { ERROR_LOADING_TASK_STATUS, SCHEDULE_TASK_BATCH_SIZE } from "constants/schedule-monitor.constants";
import TasksRepository from "repositories/tasks/tasks.repository";
import { ITaskRowViewModel } from "types/task";
import { findDueTask } from "utils/derive-schedule-status";

type LatestTaskByScheduleId = Record<string, ITaskRowViewModel | null>;
type RecentTasksByScheduleId = Record<string, ITaskRowViewModel[]>;

interface IUseLatestTaskByScheduleIdResult {
    tasksByScheduleId: LatestTaskByScheduleId;
    /**
     * The full merged (up to 18) tasks per schedule that `tasksByScheduleId` was narrowed from -
     * used to check whether a still-active schedule has a pending/queued task ahead of it.
     */
    recentTasksByScheduleId: RecentTasksByScheduleId;
    loading: boolean;
    error: string | null;
}

const chunk = <T,>(items: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < items.length; i += size) {
        chunks.push(items.slice(i, i + size));
    }
    return chunks;
};

/**
 * The single latest task per schedule - the front of ticket 02's merged, already newest-first
 * per-status list - which ticket 03's failed/stalled derivation and the KPI cards' failed/
 * stalled/success-rate figures compare against and read from. Fetched once per distinct set of
 * schedule ids, plus once per `refreshToken` change (the KPI cards' 60s poll bumps this even
 * when the id set is unchanged, via `useAllSchedules`'s `refreshedAt` - see ticket 04). A token
 * bump alone is treated as a background refresh: it doesn't toggle `loading` or clear stale data
 * on failure, since the fan-out is six requests per schedule and re-running it silently on a
 * poll tick shouldn't flicker a table/cards that were working fine a moment ago. A schedule
 * whose fetch fails is simply absent from the map (read as "unknown" by the caller) rather than
 * failing the batch.
 */
const useLatestTaskByScheduleId = (
    scheduleIds: string[],
    refreshToken = 0,
): IUseLatestTaskByScheduleIdResult => {
    const [tasksByScheduleId, setTasksByScheduleId] = useState<LatestTaskByScheduleId>({});
    const [recentTasksByScheduleId, setRecentTasksByScheduleId] = useState<RecentTasksByScheduleId>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const abortControllerRef = useRef<AbortController | null>(null);
    const previousIdsKeyRef = useRef<string | null>(null);
    const idsKey = scheduleIds.join(",");

    useEffect(() => {
        abortControllerRef.current?.abort();

        if (!idsKey) {
            previousIdsKeyRef.current = null;
            setTasksByScheduleId({});
            setRecentTasksByScheduleId({});
            setLoading(false);
            setError(null);
            return undefined;
        }

        const background = previousIdsKeyRef.current === idsKey;
        previousIdsKeyRef.current = idsKey;

        const controller = new AbortController();
        abortControllerRef.current = controller;

        if (!background) {
            setLoading(true);
            setError(null);
        }

        // getRecentTasksByStatus already isolates per-status failures internally (see ticket 02)
        // and resolves to `[]` rather than rejecting, so a schedule with no reachable tasks ends
        // up here as `null` (read as "unknown"), not as a rejected promise. Batches run in
        // sequence - see SCHEDULE_TASK_BATCH_SIZE - so this never fires more than one batch's worth
        // of schedules' requests at a time.
        const fetchInBatches = async (): Promise<void> => {
            const next: LatestTaskByScheduleId = {};
            const nextRecent: RecentTasksByScheduleId = {};
            const batches = chunk(idsKey.split(","), SCHEDULE_TASK_BATCH_SIZE);
            // eslint-disable-next-line no-restricted-syntax
            for (const batch of batches) {
                if (controller.signal.aborted) return;
                // Each batch depends on the previous one having freed up capacity - sequential by design.
                // eslint-disable-next-line no-await-in-loop
                const results = await Promise.all(
                    batch.map((scheduleId) =>
                        TasksRepository.getRecentTasksByStatus(scheduleId, controller.signal).then((rows) => {
                            // `rows` is sorted newest-first by scheduledDateTime, which can put a
                            // pre-queued task for a future run ahead of the last one actually
                            // attempted - masking real staleness from the failed/stalled
                            // derivation. Only a task whose run is already due can stand in for
                            // "the most recent task".
                            const now = new Date();
                            const dueTask = findDueTask(rows, now);
                            return [scheduleId, dueTask, rows] as const;
                        }),
                    ),
                );
                results.forEach(([scheduleId, latestTask, rows]) => {
                    next[scheduleId] = latestTask;
                    nextRecent[scheduleId] = rows;
                });
            }
            if (controller.signal.aborted) return;
            setTasksByScheduleId(next);
            setRecentTasksByScheduleId(nextRecent);
            setError(null);
            setLoading(false);
        };

        fetchInBatches().catch(() => {
            if (controller.signal.aborted) return;
            // A failed background poll leaves the previous data on screen, same leniency as
            // `use-all-schedules.ts` applies to its own background polls.
            if (!background) setError(ERROR_LOADING_TASK_STATUS);
            setLoading(false);
        });

        return () => {
            controller.abort();
        };
    }, [idsKey, refreshToken]);

    return { tasksByScheduleId, recentTasksByScheduleId, loading, error };
};

export default useLatestTaskByScheduleId;
